import { defaultUnits } from '@config';
import defaultOptions from '@object-generators/wood-connection/defaultOptions';
import AbstractObject from '@scene/misc/object';
import Arrow from '@scene/objects/arrow';
import { addDistanceLabel, unitTransform } from '@services/helper.service';
import * as THREE from 'three';
import Scene from "@scene";
import TextLabel from '../text';
import Nail from './nail';
import ObjectView from '@scene/views/object-view';

export default class NailGroup extends AbstractObject {

  constructor(options = {}) {
    const defaultOptions = {
      name: 'nail-group',
      type: 'nail-group'
    };

    super(options, defaultOptions);

    this._nailList = [];

    this.options = options;
    this.init();
  }
  
  get showLabels() {
    return this.options.mode === ObjectView.mode.MODE_2D || this.options.object.dimensions.showLabels;
  }

  init() {
    const spacings = {};
    for (let i = 0; i < this.options.nail.n; i++) {
      const columnIndex = i + 1;

      const hasCustomVerticalSpacing = this.options.nail.spacings && this.options.nail.spacings[`s${columnIndex}`];

      spacings[`s${columnIndex}`] = hasCustomVerticalSpacing
        ? this.options.nail.spacings[`s${columnIndex}`]
        : defaultOptions.spacing.vertical.s;

      const hasCustomHorizontalSpacing = this.options.nail.spacings && this.options.nail.spacings[`c${columnIndex}Top`];

      spacings[`c${columnIndex}Top`] = hasCustomHorizontalSpacing
        ? this.options.nail.spacings[`c${columnIndex}Top`]
        : defaultOptions.spacing.vertical.c;
    }
    this.generateNails();
  }

  generateNails() {
    //const isSingleRowConfiguration = this.options.nail.p === 1;

    // if (isSingleRowConfiguration) {
    //   this.generateHorizontalNails();
    // } else {
      this.generateVerticalNails();
    // }
  }

  generateHorizontalNails() {
    const n = this.options.nail.n;
    // const p = this.options.nail.p;
    const spacing = 15;
    const totalWidth = (n - 1) * spacing;
    const startX = -totalWidth / 2;

    const defaultPositions = Array(n).fill(0).map((_, index) => ({
      id: index + 1,
      x: startX + (index * spacing),
      y: 0
    }));

    this.options.customPositions = this.options.useCustomPositions
      ? this.options.customPositions
      : defaultPositions;

    this.generateCustomNails();
  }

  generateVerticalNails() {
    const generators = {
      [defaultOptions.generators.custom]: this.generateCustomNails.bind(this),
      [defaultOptions.generators.grid]: this.generateGridNails.bind(this)
    };

    const type = this.options.useCustomPositions
      ? defaultOptions.generators.custom
      : defaultOptions.generators.grid;

    generators[type]();
  }

  generateGridNails() {
    for (let i = 0; i < this.options.nail.p; i++) {
      for (let j = 0; j < this.options.nail.n; j++) {
        const nail = this.createNail();
        const position = this.calculateGridPosition(i, j);
        const nailNumber = i * this.options.nail.n + j + 1;

        this.addNailToGroup(nail, nailNumber, position);
      }
    }
  }

  generateCustomNails() {
    const { customPositions } = this.options;

    customPositions.forEach(coord => {
      const nail = this.createNail();
      const position = new THREE.Vector3(coord.x, 0, coord.y);

      this.addNailToGroup(nail, coord.id, position);
    });
  }

  createNail() {
    return new Nail({
      radius: this.options.nail.diameter / 2,
      height: this.options.holeDepth,
      color: defaultOptions.materials.nailColor
    });
  }

  calculateGridPosition(i, j) {
    const horizontalSpacing = this.options.nail.spacings.sc || defaultOptions.spacing.horizontal;
    const verticalSpacing = this.options.nail.spacings[`s${j + 1}`];
    const zOffset = this.getZOffset(j);

    const isEven = this.options.nail.n % 2 === 0;
    const middleIndex = Math.floor(this.options.nail.n / 2);

    const xPosition = isEven
      ? this.calculateEvenPosition(j, middleIndex, horizontalSpacing)
      : (j - middleIndex) * horizontalSpacing;

    return new THREE.Vector3(
      xPosition,
      0,
      -(i * verticalSpacing + zOffset)
    );
  }

  getZOffset(j) {
    const columnIndex = j + 1;
    const hasCustomSpacing = this.options.nail
      && this.options.nail.spacings
      && this.options.nail.spacings[`c${columnIndex}Top`] !== undefined;

    return hasCustomSpacing
      ? this.options.nail.spacings[`c${columnIndex}Top`]
      : defaultOptions.spacing.vertical.c;
  }

  calculateEvenPosition(index, middleIndex, spacing) {
    if (index < middleIndex) {
      return (index - middleIndex + 1) * spacing - spacing / 2;
    }
    return (index - middleIndex) * spacing + spacing / 2;
  }

  addNailToGroup(nail, number, position) {
    nail.setPosition(position);
    this.mesh.add(nail.mesh);
    this._nailList.push({ nail, number });
  }

  get mesh() {
    return this.object.mesh;
  }

  get nails() {
    return this._nailList;
  }

  updatePadding(newPadding) {
    const updatedPadding = {};
    Object.keys(newPadding).forEach(key => {
      if (newPadding[key] !== undefined) {
        updatedPadding[key] = newPadding[key];
      }
    });

    this.options.padding = {
      ...this.options.padding,
      ...updatedPadding
    };

    this._nailList = [];

    this.generateNails();
  }

  getNails() {
    return this._nailList;
  }

  createColumnDimensions() {
    const nailOptions = this.options.nail;
    this.labels = [];

    const cLabels = [], sLabels = [];
    for (let j = 0; j < nailOptions.n; j++) {
      const columnIndex = j + 1;
      const firstNailNumber = j + 1;
      const secondNailNumber = firstNailNumber + nailOptions.n;

      const firstNail = this._nailList.find(n => n.number === firstNailNumber);
      const secondNail = this._nailList.find(n => n.number === secondNailNumber);

      if (!firstNail) continue;

      const initialOffset = 25; // offset from the edge of the plate
      const indexOffset = 50 * j; // offset for each column
      const leftSideOffset = -this.options.plateWidth / 2 - initialOffset - indexOffset;
      const rightSideOffset = this.options.plateWidth / 2 + initialOffset + indexOffset;

      const startPos1 = firstNail.nail.mesh.position.clone();
      const firstNailWorldPos = new THREE.Vector3();
      firstNail.nail.mesh.getWorldPosition(firstNailWorldPos);
      
      let top = {
        from: {
          x: 0,
          y: 0,
          z: 0
        },
        to: {
          x: rightSideOffset,
          y: 0,
          z: 0
        }
      };
      let bottom = {
        from: {
          x: startPos1.x,
          y: startPos1.y,
          z: startPos1.z
        },
        to: {
          x: rightSideOffset,
          y: startPos1.y,
          z: startPos1.z
        }
      };

      const cDistance = this.options.nail.spacings[`c${columnIndex}Top`];
      const CLabel = addDistanceLabel({
        top,
        bottom,
        size: cDistance,
        arrowDirection: Arrow.types.direction.NegativeZ,
        arrowSize: 5,
        labelText: (!this.options || this.showLabels ? `C${columnIndex} = ` : '') + `${cDistance}`,
        labelValue: unitTransform(cDistance)
          .from(defaultUnits.DISTANCE)
          .to(Scene.units.distance),
        align: TextLabel.types.align.LEFT,
        verticalAlign: TextLabel.types.verticalAlign.CENTER,
        onChange: (value, didChange) => {
          this.dispatchOptionUpdate({
            nail: {
              spacings: {
                [`c${columnIndex}Top`]: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }, false, didChange);
        }
      });
      cLabels.push(CLabel);
      CLabel.text.position.set(
        rightSideOffset,
        0,
        -firstNailWorldPos.y - (cDistance / 2)
      );
      this.mesh.add(CLabel.object);
      this.htmlLayer.add(CLabel.text);

      if (!secondNail) continue;

      const startPos2 = secondNail.nail.mesh.position.clone();
      const secondNailWorldPos = new THREE.Vector3();
      secondNail.nail.mesh.getWorldPosition(secondNailWorldPos);
      
      top = {
        from: {
          x: startPos1.x,
          y: startPos1.y,
          z: startPos1.z
        },
        to: {
          x: leftSideOffset,
          y: startPos1.y,
          z: startPos1.z
        }
      };
      bottom = {
        from: {
          x: startPos2.x,
          y: startPos2.y,
          z: startPos2.z
        },
        to: {
          x: leftSideOffset,
          y: startPos2.y,
          z: startPos2.z
        }
      };

      const sDistance = this.options.nail.spacings[`s${columnIndex}`];
      const SLabel = addDistanceLabel({
        top,
        bottom,
        size: sDistance,
        arrowDirection: Arrow.types.direction.NegativeZ,
        arrowSize: 5,
        labelText: (!this.options || this.showLabels ? `S${columnIndex} = ` : '') + `${sDistance}`,
        labelValue: unitTransform(sDistance)
          .from(defaultUnits.DISTANCE)
          .to(Scene.units.distance),
        align: TextLabel.types.align.RIGHT,
        verticalAlign: TextLabel.types.verticalAlign.CENTER,
        onChange: (value, didChange) => {
          this.dispatchOptionUpdate({
            nail: {
              spacings: {
                [`s${columnIndex}`]: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
              }
            }
          }, false, didChange);
        }
      });
      sLabels.push(SLabel);
      SLabel.text.position.set(
        leftSideOffset,
        0,
        -firstNailWorldPos.y + (sDistance / 2)
      );
        
      this.mesh.add(SLabel.object);
      this.htmlLayer.add(SLabel.text);
    }

    return {
      cLabels,
      sLabels
    };
  }

  updateLabels(camera, renderer) {
    if (this.labels && this.labels.length > 0) {
      this.labels.forEach(label => {
        if (label && label.render) {
          label.render(camera, renderer);
        }
      });
    }
  }

  dispose() {
    if (this.labels) {
      this.labels.forEach(label => {
        if (label.domElement && label.domElement.parentNode) {
          label.domElement.parentNode.removeChild(label.domElement);
        }
      });
      this.labels = [];
    }
  }

  clear() {
    super.clear();
    this.htmlLayer.clear();
    this.layer2D.clear();
    this._nailList = [];
    this.labels = [];
  }

  get htmlLayer() {
    return this.object.htmlLayer || super.htmlLayer;
  }

} 



// WEBPACK FOOTER //
// ./src/object-view/objects/nail/nail-group.js